import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

// User roles
export const USER_ROLES = {
  ADMIN: 'admin',
  FREE: 'free',
  PAID: 'paid',
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // অ্যাডমিন ইমেইল চেক করার হেল্পার ফাংশন
  const checkAdminStatus = (userData) => {
    // এখানে admin@connectai.com সরিয়ে emniteir@gmail.com যোগ করা হয়েছে
    const adminEmails = ['plzplus@gmail.com', 'emniteir@gmail.com'];
    if (userData && adminEmails.includes(userData.email)) {
      return { ...userData, role: USER_ROLES.ADMIN };
    }
    return userData;
  };

  // ১. fetchUser ফাংশন
  const fetchUser = useCallback(async () => {
    const savedToken = localStorage.getItem('connectai_token');
    if (!savedToken) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await authAPI.getMe();
      let userData = response.data;
      
      // অ্যাডমিন চেক
      userData = checkAdminStatus(userData);
      
      setUser(userData);
      setAuthError(null);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setAuthError(error.response?.data?.detail || 'Session expired');
      localStorage.removeItem('connectai_token');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ২. login ফাংশন
  const login = async (email, password) => {
    setAuthError(null);
    try {
      const response = await authAPI.login({ email, password });
      let { token: newToken, user: userData } = response.data;
      
      // অ্যাডমিন চেক
      userData = checkAdminStatus(userData);
      
      localStorage.setItem('connectai_token', newToken);
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Login failed';
      setAuthError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // ৩. register ফাংশন
  const register = async (name, email, password) => {
    setAuthError(null);
    try {
      const response = await authAPI.register({ name, email, password });
      let { token: newToken, user: userData } = response.data;
      
      userData = checkAdminStatus(userData);
      
      localStorage.setItem('connectai_token', newToken);
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Registration failed';
      setAuthError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // ৪. logout ফাংশন
  const logout = useCallback(() => {
    localStorage.removeItem('connectai_token');
    setUser(null);
    setAuthError(null);
  }, []);

  // ৫. updateUser ফাংশন
  const updateUser = useCallback((updates) => {
    setUser((prev) => prev ? { ...prev, ...updates } : null);
  }, []);

  // ৬. refreshUser ফাংশন
  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.getMe();
      let userData = response.data;
      
      userData = checkAdminStatus(userData);
      
      setUser(userData);
      setAuthError(null);
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setAuthError('Failed to refresh user data');
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Computed role checks
  const isAdmin = user?.role === USER_ROLES.ADMIN;
  const isPaid = user?.role === USER_ROLES.PAID || user?.role === USER_ROLES.ADMIN;
  const isFree = user?.role === USER_ROLES.FREE || (!user?.role);
  const isAuthenticated = !!user;

  const canAccessPrompt = useCallback((prompt) => {
    if (!prompt) return false;
    if (!user) return false;
    if (isAdmin) return true;
    if (!prompt.is_premium && !prompt.isPremium) return true;
    if (isPaid) return true;
    return false;
  }, [user, isAdmin, isPaid]);

  const getUserRole = useCallback(() => {
    if (!user) return null;
    return user.role || USER_ROLES.FREE;
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        authError,
        login,
        register,
        logout,
        updateUser,
        refreshUser,
        isAdmin,
        isPaid,
        isFree,
        canAccessPrompt,
        getUserRole,
        USER_ROLES,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
